import { useEffect, useState } from 'react'
import { api, type Einrichtungsstand, type Einrichtungspunkt } from '../net/apiClient.js'
import { ActionButton } from '../ui/ActionButton.js'
import { notify } from '../ui/Toasts.js'
import { uuidv7 } from '../net/ids.js'
import { fehlerText } from '../net/errors.js'

/**
 * Was diesem Betrieb zur Einrichtung noch fehlt.
 *
 * Ein neuer Betrieb meldet sich zum ersten Mal an und sieht eine Software,
 * die alles kann und nichts weiss. Ohne Führung endet das in einem Anruf –
 * oder in einer Rechnung, die ohne Anschrift und ohne IBAN hinausgeht.
 *
 * Drei Entscheidungen prägen diesen Bildschirm:
 *
 *   Er zeigt den Zustand, nicht den Fortschritt. Keine Prozentzahl, kein
 *   Balken. Wer die IBAN wieder löscht, sieht den Punkt sofort wieder offen –
 *   ein Fortschrittsbalken bliebe voll.
 *
 *   Er trennt «blockierend» von «empfohlen». Ohne IBAN geht keine Rechnung
 *   hinaus; ohne zweiten Inhaber geht alles, nur ist es unklug. Beides gleich
 *   dringend zu malen heisst, dass beides überlesen wird.
 *
 *   Jeder offene Punkt sagt, was zu tun ist. Ein roter Punkt ohne Satz
 *   daneben ist ein Vorwurf ohne Anleitung.
 */

const FARBE: Record<Einrichtungspunkt['stand'], string> = {
  erledigt: 'o-stand erledigt',
  fehlt: 'o-stand fehlt',
  empfohlen: 'o-stand empfohlen',
  blockierend: 'o-stand blockierend'
}

const WORT: Record<Einrichtungspunkt['stand'], string> = {
  erledigt: 'erledigt',
  fehlt: 'offen',
  empfohlen: 'empfohlen',
  blockierend: 'nötig'
}

export function Einrichtung(): JSX.Element {
  const [daten, setDaten] = useState<Einrichtungsstand | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [vorlagen, setVorlagen] = useState<
    Array<{ key: string; label: string; positionen: number; ohnePreis: number }>
  >([])
  const [wahl, setWahl] = useState('')

  async function laden(): Promise<void> {
    setDaten(await api.einrichtung())
  }

  useEffect(() => {
    void laden().catch((e) => setFehler(fehlerText(e)))
    void api
      .katalogVorlagen()
      .then((a) => setVorlagen(a.vorlagen))
      .catch(() => undefined)
  }, [])

  /**
   * Einen Startkatalog übernehmen.
   *
   * Steht hier und nicht nur bei der Registrierung: ein Betrieb, der eine
   * zweite Sparte aufnimmt, holt sich deren Grundpositionen dazu — und einer,
   * der bei der Anmeldung «keine passt» gewählt hat, kommt später doch noch
   * an einen Anfang.
   */
  async function vorlageUebernehmen(): Promise<void> {
    if (!wahl) return
    const a = await api.katalogVorlageUebernehmen(wahl, uuidv7())
    notify.ok(
      a.uebernommen === 0
        ? 'Nichts zu übernehmen — alle Positionen dieser Vorlage sind schon da.'
        : `${a.uebernommen} Positionen übernommen.` +
          (a.ohnePreis > 0 ? ` Davon ${a.ohnePreis} ohne Ansatz — die gehören dir.` : '')
    )
    await laden()
  }

  if (fehler) {
    return (
      <div className="o-band alarm" id="einrichtung-fehler">
        <span>{fehler}</span>
      </div>
    )
  }
  if (!daten) return <p className="o-soft">Wird geprüft …</p>

  return (
    <div id="abschnitt-einrichtung">
      <div className="o-card">
        <div className="o-card-head">
          <h3>Einrichtung</h3>
          <span className="o-soft">Geprüft beim Öffnen, nicht gespeichert</span>
        </div>
        <div className="o-pad">
          {daten.bereit ? (
            <p className="o-band" id="einrichtung-bereit">
              <span>
                Alles Nötige steht. Was jetzt noch offen ist, sind Empfehlungen — du kannst
                arbeiten.
              </span>
            </p>
          ) : (
            <p className="o-soft" style={{ marginTop: 0 }}>
              <strong>{daten.blockierend}</strong> {daten.blockierend === 1 ? 'Punkt' : 'Punkte'}{' '}
              halten dich auf, <strong>{daten.offen}</strong> sind offen,{' '}
              <strong>{daten.empfohlen}</strong> sind Empfehlungen. Was blockiert, steht oben in
              jedem Bereich.
            </p>
          )}
        </div>
      </div>

      {/*
        Der Startkatalog steht bei der Einrichtung und nicht nur im
        Katalogbildschirm — hier merkt ein Betrieb, dass ihm einer fehlt.
      */}
      {vorlagen.length > 0 && (
        <div className="o-card" id="einrichtung-vorlagen" style={{ marginTop: 12 }}>
          <div className="o-card-head">
            <h3>Startkatalog übernehmen</h3>
            <span className="o-soft">Ergänzt, ersetzt nie</span>
          </div>
          <div className="o-pad">
            <p className="o-soft" style={{ marginTop: 0 }}>
              Raporto liefert für jedes Gewerbe Grundpositionen mit. Sie sind ein Anfang, kein
              Gesetz: jede Zeile lässt sich ändern, abschalten oder ersetzen. Was du schon hast,
              bleibt unangetastet — auch wenn es anders heisst oder anders kostet.
            </p>
            <div className="o-grid o-grid-2">
              <div className="o-field">
                <label htmlFor="vorlage-wahl">Gewerbe</label>
                <select
                  id="vorlage-wahl"
                  value={wahl}
                  onChange={(e) => setWahl(e.target.value)}
                >
                  <option value="">Bitte wählen</option>
                  {vorlagen.map((v) => (
                    <option key={v.key} value={v.key}>
                      {v.label} — {v.positionen} Positionen
                    </option>
                  ))}
                </select>
                {wahl && (
                  <span className="hint">
                    {(() => {
                      const v = vorlagen.find((x) => x.key === wahl)
                      if (!v) return null
                      return v.ohnePreis === 0
                        ? 'Alle Positionen tragen einen Ansatz aus einem echten Bestand.'
                        : `${v.ohnePreis} der ${v.positionen} Positionen kommen ohne Ansatz — Raporto rät keine Preise.`
                    })()}
                  </span>
                )}
              </div>
              <div className="o-field" style={{ alignSelf: 'end' }}>
                <ActionButton
                  id="btn-vorlage-uebernehmen"
                  disabled={!wahl}
                  labels={{ idle: 'Übernehmen', busy: 'Übernimmt …', success: 'Übernommen' }}
                  onAction={vorlageUebernehmen}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {daten.bereiche.map((b) => (
        <div className="o-card" key={b.id} id={`einrichtung-${b.id}`} style={{ marginTop: 12 }}>
          <div className="o-card-head">
            <h3>{b.titel}</h3>
          </div>
          <div className="o-pad">
            {/*
              Sortiert nach Dringlichkeit, nicht nach Reihenfolge im Code.
              Was aufhält, steht oben; was erledigt ist, tritt zurück.
            */}
            {[...b.punkte]
              .sort((x, y) => rang(x.stand) - rang(y.stand))
              .map((p) => (
                <div className="o-angabe" key={p.id} id={`einrichtung-punkt-${p.id}`}>
                  <span>
                    <span className={FARBE[p.stand]} aria-hidden="true" /> {p.text}
                  </span>
                  <strong className={p.stand === 'erledigt' ? undefined : 'fehlt'}>
                    {WORT[p.stand]}
                  </strong>
                  {p.hinweis && <em className="hint">{p.hinweis}</em>}
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function rang(stand: Einrichtungspunkt['stand']): number {
  return stand === 'blockierend' ? 0 : stand === 'fehlt' ? 1 : stand === 'empfohlen' ? 2 : 3
}
